/* SEO autopilot settings model (owner addition 24-10-2026: caps per day/week/month,
 * cadence tiers, pauses, global stop, full-auto OFF, estimated raw API cost).
 * DOM-free and dependency-free so it runs under `node --test --experimental-strip-types`.
 * The server re-validates everything (AutopilotConfig::validate); these checks only
 * give the owner an early, matching message. */

export interface SeoAutopilotCaps { daily_auto: boolean; per_day: number; per_week: number; per_month: number }
export interface SeoAutopilotCadence {
  not_ranking_per_week: number;
  band_4_10_per_week: number;
  band_2_3_per_week: number;
  top1_interval_weeks: number;
  top1_hold_checks: number;
  step_up_checks: number;
  autopause_weeks: number;
  guard_reject_pause_after: number;
  archive_per_week: number;
}
export interface SeoAutopilotSettings {
  target_id: number;
  global_stop: boolean;
  full_auto: boolean;
  caps: SeoAutopilotCaps;
  cadence: SeoAutopilotCadence;
  paused_listings: string[];
  paused_tenants: string[];
}
export interface SeoAutopilotListing {
  listing_id: string;
  listing_title: string;
  listing_url?: string;
  tenant_slug: string;
  keyword?: string;
  keyword_source?: string;
  position?: number | null;
  tier?: string | null;
  eligible: boolean;
  paused: boolean;
  auto_paused?: boolean;
  auto_paused_reason?: string;
  reason?: string;
}
export interface SeoAutopilotCost {
  label: string; model: string; source: string; basis: string; eligible_listings: number;
  posts_per_week: number; posts_per_week_uncapped: number; posts_per_month: number;
  per_article: string | null; per_article_worst: string | null; per_week: string | null;
  per_month: string | null; per_month_worst: string | null; evidence_api: string; fixed_weekly?: string;
}
export interface SeoAutopilotScheduleDay {
  date: string; day: string; listings: string[];
  items: Array<{ listing_id: string; title: string; primary: string | null; secondary: string[] }>;
}
export interface SeoAutopilotSchedule {
  weekly_quota: number; daily_auto: number; daily_effective: number; warnings: string[]; preview: SeoAutopilotScheduleDay[];
}
export interface SeoAutopilotStatus {
  schedule?: SeoAutopilotSchedule;
  tracking?: { added: number; max: number; recent: Array<{ keyword: string; listing_id: string; at: string }> };
  settings: SeoAutopilotSettings;
  defaults: SeoAutopilotSettings;
  flags: Record<string, boolean>;
  stops: string[];
  running: boolean;
  delivery: string;
  full_auto_available: boolean;
  plan_at: string | null;
  listings: SeoAutopilotListing[];
  alerts: Array<{ at: string; type: string; listing_id: string; detail: string }>;
  audit: Array<{ at: string; principal: string; changes: Record<string, { from: unknown; to: unknown }> }>;
  cost_estimate: SeoAutopilotCost | null;
}

export const CADENCE_LIMITS: Record<keyof SeoAutopilotCadence, [number, number]> = {
  not_ranking_per_week: [1, 3], band_4_10_per_week: [1, 3], band_2_3_per_week: [0, 2],
  top1_interval_weeks: [1, 12], top1_hold_checks: [1, 8], step_up_checks: [1, 8], autopause_weeks: [2, 52],
  guard_reject_pause_after: [1, 10],
  archive_per_week: [0, 3],
};

export const CADENCE_LABELS: Record<keyof SeoAutopilotCadence, string> = {
  not_ranking_per_week: 'Not ranking or worse than 10 — posts a week',
  band_4_10_per_week: 'Position 4–10 — posts a week',
  band_2_3_per_week: 'Position 2–3 — posts a week',
  top1_interval_weeks: 'Held #1 — one post every N weeks',
  top1_hold_checks: 'Weekly checks at #1 before slowing down',
  step_up_checks: 'Weekly checks worse than 3 before speeding up again',
  autopause_weeks: 'Weeks at the top cadence with no improvement before auto-pause',
  guard_reject_pause_after: 'Rejected articles in a row before a listing auto-pauses',
  archive_per_week: 'Each indexed category + city page — posts a week',
};

export const STOP_LABELS: Record<string, string> = {
  autopilot_global_stop_on: 'Autopilot stop is ON',
  'flag_off:CONTENT_ENGINE_ENABLED': 'Content engine switched off (CONTENT_ENGINE_ENABLED)',
  'flag_off:CONNECTOR_ENABLED': 'WordPress connector switched off (CONNECTOR_ENABLED)',
};

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/** First client-side problem with a settings draft, or null (mirrors AutopilotConfig::validate). */
export function settingsError(s: SeoAutopilotSettings): string | null {
  if (typeof s.caps.daily_auto !== 'boolean') return 'Daily cap mode must be on or off.';
  for (const k of ['per_day', 'per_week', 'per_month'] as const) {
    const v = s.caps[k];
    if (!isInt(v) || v < 0 || v > 5000) return `Cap "${k.replace('per_', 'per ')}" must be a whole number from 0 to 5000.`;
  }
  if (!s.caps.daily_auto && s.caps.per_day > s.caps.per_week) return 'The daily cap cannot be higher than the weekly cap.';
  if (s.caps.per_week > s.caps.per_month) return 'The weekly cap cannot be higher than the monthly cap.';
  for (const k of Object.keys(CADENCE_LIMITS) as Array<keyof SeoAutopilotCadence>) {
    const [lo, hi] = CADENCE_LIMITS[k];
    const v = s.cadence[k];
    if (!isInt(v) || v < lo || v > hi) return `${CADENCE_LABELS[k]}: use a whole number from ${lo} to ${hi}.`;
  }
  if (s.full_auto) return 'Full automatic publishing is not available yet.';
  return null;
}

/** Minimal POST payload: only the sections that changed (the server merges + audits). */
export function settingsPatch(original: SeoAutopilotSettings, draft: SeoAutopilotSettings): Partial<SeoAutopilotSettings> {
  const out: Partial<SeoAutopilotSettings> = {};
  if (draft.global_stop !== original.global_stop) out.global_stop = draft.global_stop;
  if (JSON.stringify(draft.caps) !== JSON.stringify(original.caps)) out.caps = { ...draft.caps };
  if (JSON.stringify(draft.cadence) !== JSON.stringify(original.cadence)) out.cadence = { ...draft.cadence };
  const sorted = (a: string[]) => [...a].sort().join('|');
  if (sorted(draft.paused_listings) !== sorted(original.paused_listings)) out.paused_listings = [...draft.paused_listings];
  if (sorted(draft.paused_tenants) !== sorted(original.paused_tenants)) out.paused_tenants = [...draft.paused_tenants];
  return out;
}

/** True for a category x city archive target (id "archive:..."). */
export function isArchiveId(id: string): boolean {
  return id.startsWith('archive:');
}

export function toggleIn(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

/** Human label for a cadence tier. */
export function tierLabel(tier: string | null | undefined, c: SeoAutopilotCadence): string {
  switch (tier) {
    case 'top1_held': return `Held #1 — 1 every ${c.top1_interval_weeks} weeks`;
    case 'band_2_3': return `${c.band_2_3_per_week} a week`;
    case 'band_4_10': return `${c.band_4_10_per_week} a week`;
    case 'not_ranking': return `${c.not_ranking_per_week} a week`;
    case 'archive': return `${c.archive_per_week} a week (category page)`;
    default: return '—';
  }
}
